import { cloneElement, isValidElement } from 'react'
import { Popup } from '@/ui/kit'
import { cn, nextId } from './internal/utils'
import { isPresetColor, PRESET_COLORS } from './internal/presets'
import { useTrigger } from './internal/useTrigger'

/**
 * antd Tooltip. Clones the single child to attach the trigger handlers and
 * `aria-describedby`, so keyboard and screen-reader users get the same hint.
 */
const Tooltip = ({
  title,
  overlay,
  placement = 'top',
  trigger = 'hover',
  open,
  visible,
  defaultOpen,
  onOpenChange,
  mouseEnterDelay = 0.1,
  mouseLeaveDelay = 0.1,
  color,
  overlayClassName,
  overlayStyle,
  overlayInnerStyle,
  zIndex,
  children,
  ...rest
}) => {
  const content = title ?? overlay
  const { isOpen, anchorRef, popupRef, anchorProps, popupHoverProps } = useTrigger({
    trigger: Array.isArray(trigger) ? [...trigger, 'focus'] : [trigger, 'focus'],
    open: open ?? visible,
    defaultOpen,
    onOpenChange,
    mouseEnterDelay,
    mouseLeaveDelay,
    disabled: content == null || content === '',
  })

  const tooltipId = nextId('tooltip')
  const background = color
    ? isPresetColor(color)
      ? PRESET_COLORS[color]
      : color
    : 'rgba(0, 0, 0, 0.85)'

  const child = isValidElement(children) ? (
    cloneElement(children, {
      ref: anchorRef,
      'aria-describedby': isOpen ? tooltipId : undefined,
      ...anchorProps,
    })
  ) : (
    <span ref={anchorRef} {...anchorProps}>
      {children}
    </span>
  )

  return (
    <>
      {child}
      <Popup
        anchorRef={anchorRef}
        popupRef={popupRef}
        open={isOpen}
        placement={placement}
        zIndex={zIndex}
        id={tooltipId}
        role="tooltip"
        className={cn('ui-tooltip max-w-xs', overlayClassName)}
        style={overlayStyle}
        {...popupHoverProps}
      >
        <div
          className="ui-tooltip-inner rounded-[var(--radius-sm)] px-3 py-1.5 text-xs text-[var(--on-primary)]"
          style={{ backgroundColor: background, ...overlayInnerStyle }}
          {...rest}
        >
          {typeof content === 'function' ? content() : content}
        </div>
      </Popup>
    </>
  )
}

export default Tooltip
